import Checkbox from '@orn/shared-web/components/Checkbox';
import * as React from 'react';

import { FeatureGateAudience } from '../../__generated__/globalTypes';
import Input from '../../components/Input';

/**
 * Shared form fields for creating + editing a feature gate. Pure
 * presentational — owns layout + tooltips + token text only. The
 * containing scene owns state, validation, mutations, and confirmation
 * dialogs. Splitting this out keeps `NewFeatureGateScene` and
 * `EditFeatureGateScene` from drifting visually as the form gains
 * fields in v2.
 */

export interface IFeatureGateFormValues {
  name: string;
  description: string;
  adminPct: number;
  everyonePct: number;
  isEnabled: boolean;
  audience: FeatureGateAudience;
  defaultWhenError: boolean;
}

export interface IFeatureGateFormErrors {
  name?: string;
  rollout?: string;
}

interface IFeatureGateFormProps {
  values: IFeatureGateFormValues;
  errors: IFeatureGateFormErrors;
  onChange: (patch: Partial<IFeatureGateFormValues>) => void;
  /** When true, the name + audience fields are read-only (edit mode). */
  readOnlyNameAndAudience: boolean;
}

/**
 * Maps two boolean checkboxes (user app / admin app visibility) to the
 * four `FeatureGateAudience` values. Both off → server only; either or
 * both on combine into the matching client/all enum.
 */
export function audienceFromCheckboxes(userClient: boolean, adminClient: boolean): FeatureGateAudience {
  if (userClient && adminClient) {
    return FeatureGateAudience.AllClients;
  }
  if (userClient) {
    return FeatureGateAudience.WebClient;
  }
  if (adminClient) {
    return FeatureGateAudience.MopsWebClient;
  }
  return FeatureGateAudience.ServerOnly;
}

/** Inverse of `audienceFromCheckboxes` — used to populate the form on edit. */
export function audienceToCheckboxes(audience: FeatureGateAudience): { userClient: boolean; adminClient: boolean } {
  switch (audience) {
    case FeatureGateAudience.AllClients:
      return { userClient: true, adminClient: true };
    case FeatureGateAudience.WebClient:
      return { userClient: true, adminClient: false };
    case FeatureGateAudience.MopsWebClient:
      return { userClient: false, adminClient: true };
    case FeatureGateAudience.ServerOnly:
      return { userClient: false, adminClient: false };
    default:
      // Unreachable — every enum variant is handled above. Defensive
      // fallback in case the codegen enum gains a new variant before the
      // form is updated.
      return { userClient: false, adminClient: false };
  }
}

const FeatureGateForm = ({ values, errors, onChange, readOnlyNameAndAudience }: IFeatureGateFormProps) => {
  const [showAdvanced, setShowAdvanced] = React.useState(values.defaultWhenError);
  const audience = audienceToCheckboxes(values.audience);

  const handlePctInput = (key: 'adminPct' | 'everyonePct') => (raw: string) => {
    const next = clampPct(raw);
    onChange({ [key]: next } as Partial<IFeatureGateFormValues>);
  };

  return (
    <div className="flex flex-col gap-5">
      {/* Identity */}
      <div className="flex flex-col gap-3">
        <Input
          className="max-w-md"
          label="Name"
          placeholder="my-new-gate"
          value={values.name}
          onChange={(value) => onChange({ name: value })}
          isInvalid={Boolean(errors.name)}
          hint={errors.name ?? 'Lowercase, digits, and hyphens. Treat as public — never embed PII or secrets.'}
          readOnly={readOnlyNameAndAudience}
          inputClassName={readOnlyNameAndAudience ? 'opacity-70' : undefined}
          autoComplete="off"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
        />
        <Input
          className="max-w-md"
          label="Description"
          placeholder="What this gate controls."
          value={values.description}
          onChange={(value) => onChange({ description: value })}
          hint="Operator-facing notes. Not surfaced to consumers."
        />
      </div>

      {/* Rollout sliders rendered as integer inputs — MOPS aesthetic
          favors dense numeric inputs over decorative sliders. */}
      <fieldset className="border-border-primary flex flex-col gap-3 border-l-2 px-4 py-2">
        <legend className="text-fg-secondary px-1 text-xs font-medium uppercase tracking-wider">Rollout</legend>
        <div className="flex flex-wrap gap-6">
          <PercentField
            label="Admins rollout %"
            value={values.adminPct}
            onChange={handlePctInput('adminPct')}
            hint="Share of admin users who pass."
          />
          <PercentField
            label="Everyone rollout %"
            value={values.everyonePct}
            onChange={handlePctInput('everyonePct')}
            hint="Share of all users (including admins) who pass."
          />
        </div>
        {errors.rollout && <span className="text-fg-error-primary text-xs">{errors.rollout}</span>}
      </fieldset>

      {/* Kill switch */}
      <div className="flex items-start gap-3">
        <Checkbox
          isSelected={values.isEnabled}
          onChange={(next) => onChange({ isEnabled: next })}
          label="Enabled"
          hint="When off, the gate fails closed for every viewer regardless of rule."
        />
      </div>

      {/* Audience visibility */}
      <fieldset className="border-border-primary flex flex-col gap-2 border-l-2 px-4 py-2">
        <legend className="text-fg-secondary px-1 text-xs font-medium uppercase tracking-wider">Visibility</legend>
        <Checkbox
          isSelected={audience.userClient}
          onChange={(next) => onChange({ audience: audienceFromCheckboxes(next, audience.adminClient) })}
          isDisabled={readOnlyNameAndAudience}
          label="Visible to user app"
          hint="The consumer web app sees this gate's name + pass/fail."
        />
        <Checkbox
          isSelected={audience.adminClient}
          onChange={(next) => onChange({ audience: audienceFromCheckboxes(audience.userClient, next) })}
          isDisabled={readOnlyNameAndAudience}
          label="Visible to admin app"
          hint="The mops admin app sees this gate's name + pass/fail."
        />
        {readOnlyNameAndAudience && (
          <span className="text-fg-tertiary text-xs italic">
            Audience is set at creation and cannot be changed. Delete and recreate to flip a gate's audience.
          </span>
        )}
      </fieldset>

      {/* Advanced disclosure — deliberately a raw <button>, not the mops-web
          Button component. Disclosure toggles read as inline text affordances
          rather than CTAs, and Button's color/size/border variants would fight
          that aesthetic. The accessibility surface (`aria-expanded`,
          focus ring) is wired explicitly. */}
      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={() => setShowAdvanced((v) => !v)}
          className="text-fg-tertiary hover:text-fg-primary focus-visible:outline-border-brand inline-flex w-fit items-center gap-1.5 text-xs uppercase tracking-wider focus-visible:outline-2 focus-visible:-outline-offset-1"
          aria-expanded={showAdvanced}
        >
          <span>{showAdvanced ? '▾' : '▸'}</span>
          <span>Advanced</span>
        </button>
        {showAdvanced && (
          <fieldset className="border-border-primary flex flex-col gap-2 border-l-2 px-4 py-2">
            <legend className="text-fg-secondary px-1 text-xs font-medium uppercase tracking-wider">
              Default when evaluation fails
            </legend>
            <Checkbox
              isSelected={values.defaultWhenError}
              onChange={(next) => onChange({ defaultWhenError: next })}
              label="Pass by default on rule-eval error"
              hint="Off (fail-closed) is the safe choice for new gates. Flip on only for gates whose features should stay reachable through evaluation hiccups."
            />
          </fieldset>
        )}
      </div>
    </div>
  );
};

interface IPercentFieldProps {
  label: string;
  value: number;
  onChange: (raw: string) => void;
  hint?: string;
}

const PercentField = ({ label, value, onChange, hint }: IPercentFieldProps) => (
  <Input
    className="w-44"
    type="number"
    min={0}
    max={100}
    step={1}
    label={label}
    value={String(value)}
    onChange={onChange}
    hint={hint}
  />
);

/**
 * Clamps a raw input string to an integer in [0, 100]. Empty input is
 * treated as 0 so the form never has a NaN slot. Decimals truncate to
 * the nearest integer toward zero — operators paste `12.5` and the
 * field snaps to `12`, matching the v1 spec's integer-only contract.
 */
function clampPct(raw: string): number {
  if (raw.trim() === '') {
    return 0;
  }
  const n = Number(raw);
  if (!Number.isFinite(n)) {
    return 0;
  }
  return Math.min(100, Math.max(0, Math.trunc(n)));
}

export default FeatureGateForm;
